import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPin, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { formatPrice, generateOrderId } from '../../lib/utils';
import { collection, addDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import toast from 'react-hot-toast';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cartItems, appliedCoupon, getCartTotal, getCartDiscount, getFinalTotal, clearCart } = useCart();
  const { user, sendOTP, verifyOTP } = useAuth();
  
  const [step, setStep] = useState(1);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  
  const [deliveryAddress, setDeliveryAddress] = useState({
    name: '',
    phone: '',
    address: '',
    landmark: '',
    city: '',
    pincode: ''
  });
  
  const [couponCode, setCouponCode] = useState('');
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);

  useEffect(() => {
    if (cartItems.length === 0) {
      navigate('/');
      return;
    }
    
    fetchAvailableCoupons();
  }, [cartItems, navigate]);

  const fetchAvailableCoupons = async () => {
    try {
      const q = query(collection(db, 'coupons'), where('isActive', '==', true));
      const querySnapshot = await getDocs(q);
      const coupons = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAvailableCoupons(coupons);
    } catch (error) {
      console.error('Error fetching coupons:', error);
    }
  };

  const handleSendOTP = async () => {
    if (!phoneNumber) {
      toast.error('Please enter your phone number');
      return;
    }

    try {
      const result = await sendOTP(`+91${phoneNumber}`);
      setConfirmationResult(result);
      setIsOtpSent(true);
      toast.success('OTP sent successfully!');
    } catch (error) {
      console.error('Error sending OTP:', error);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp) {
      toast.error('Please enter the OTP');
      return;
    }

    setIsVerifying(true);
    try {
      await verifyOTP(confirmationResult, otp);
      setStep(2);
      toast.success('Phone verified successfully!');
    } catch (error) {
      console.error('Error verifying OTP:', error);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode) {
      toast.error('Please enter a coupon code');
      return;
    }

    setIsApplyingCoupon(true);
    try {
      const coupon = availableCoupons.find(c => c.code.toLowerCase() === couponCode.toLowerCase());
      
      if (!coupon) {
        toast.error('Invalid coupon code');
        return;
      }

      const subtotal = getCartTotal();
      if (coupon.minOrder && subtotal < coupon.minOrder) {
        toast.error(`Minimum order amount is ${formatPrice(coupon.minOrder)}`);
        return;
      }

      // Apply coupon logic would go here
      toast.success(`Coupon "${coupon.code}" applied successfully!`);
      setCouponCode('');
    } catch (error) {
      console.error('Error applying coupon:', error);
      toast.error('Failed to apply coupon');
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const handlePlaceOrder = async () => {
    if (!deliveryAddress.name || !deliveryAddress.phone || !deliveryAddress.address || !deliveryAddress.city || !deliveryAddress.pincode) {
      toast.error('Please fill in all required address fields');
      return;
    }

    setIsPlacingOrder(true);
    try {
      const orderId = generateOrderId();
      const orderData = {
        orderId,
        userId: user.uid,
        items: cartItems,
        deliveryAddress,
        subtotal: getCartTotal(),
        discount: getCartDiscount(),
        total: getFinalTotal(),
        appliedCoupon: appliedCoupon,
        status: 'pending',
        paymentStatus: 'pending',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Save order to Firestore
      const docRef = await addDoc(collection(db, 'orders'), orderData);
      
      // Clear cart
      clearCart();
      
      // Redirect to payment
      navigate(`/payment/${docRef.id}`);
      
      toast.success('Order placed successfully!');
    } catch (error) {
      console.error('Error placing order:', error);
      toast.error('Failed to place order. Please try again.');
    } finally {
      setIsPlacingOrder(false);
    }
  };

  const LoginStep = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-6"
    >
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Login to Continue</h2>
        <p className="text-gray-600">Enter your phone number to receive OTP</p>
      </div>

      <div className="space-y-4">
        <div>
          <Label htmlFor="phone">Phone Number</Label>
          <div className="flex space-x-2">
            <div className="flex items-center px-3 py-2 border border-gray-300 rounded-lg bg-gray-50">
              <span className="text-gray-600">+91</span>
            </div>
            <Input
              id="phone"
              type="tel"
              placeholder="Enter your phone number"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className="flex-1"
            />
          </div>
        </div>

        {!isOtpSent ? (
          <Button
            onClick={handleSendOTP}
            className="w-full btn-primary"
          >
            Send OTP
          </Button>
        ) : (
          <div className="space-y-4">
            <div>
              <Label htmlFor="otp">Enter OTP</Label>
              <Input
                id="otp"
                type="text"
                placeholder="Enter 6-digit OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                maxLength={6}
              />
            </div>
            <Button
              onClick={handleVerifyOTP}
              disabled={isVerifying}
              className="w-full btn-primary"
            >
              {isVerifying ? 'Verifying...' : 'Verify OTP'}
            </Button>
          </div>
        )}
      </div>
    </motion.div>
  );

  const AddressStep = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-6"
    >
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Delivery Address</h2>
        <p className="text-gray-600">Enter your delivery details</p>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="name">Full Name *</Label>
            <Input
              id="name"
              value={deliveryAddress.name}
              onChange={(e) => setDeliveryAddress(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Enter your full name"
            />
          </div>
          <div>
            <Label htmlFor="phone">Phone Number *</Label>
            <Input
              id="phone"
              value={deliveryAddress.phone}
              onChange={(e) => setDeliveryAddress(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="Enter phone number"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="address">Address *</Label>
          <Input
            id="address"
            value={deliveryAddress.address}
            onChange={(e) => setDeliveryAddress(prev => ({ ...prev, address: e.target.value }))}
            placeholder="House/Flat No., Building, Street"
          />
        </div>

        <div>
          <Label htmlFor="landmark">Landmark</Label>
          <Input
            id="landmark"
            value={deliveryAddress.landmark}
            onChange={(e) => setDeliveryAddress(prev => ({ ...prev, landmark: e.target.value }))}
            placeholder="Nearby landmark (optional)"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="city">City *</Label>
            <Input
              id="city"
              value={deliveryAddress.city}
              onChange={(e) => setDeliveryAddress(prev => ({ ...prev, city: e.target.value }))}
              placeholder="Enter city"
            />
          </div>
          <div>
            <Label htmlFor="pincode">Pincode *</Label>
            <Input
              id="pincode"
              value={deliveryAddress.pincode}
              onChange={(e) => setDeliveryAddress(prev => ({ ...prev, pincode: e.target.value }))}
              placeholder="Enter pincode"
            />
          </div>
        </div>
      </div>

      <div className="flex space-x-3">
        <Button
          onClick={() => setStep(1)}
          variant="outline"
          className="flex-1"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button
          onClick={() => setStep(3)}
          className="flex-1 btn-primary"
        >
          Continue
        </Button>
      </div>
    </motion.div>
  );

  const CouponStep = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-6"
    >
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Apply Coupon</h2>
        <p className="text-gray-600">Enter coupon code or choose from available offers</p>
      </div>

      <div className="space-y-4">
        <div className="flex space-x-2">
          <Input
            placeholder="Enter coupon code"
            value={couponCode}
            onChange={(e) => setCouponCode(e.target.value)}
            className="flex-1"
          />
          <Button
            onClick={handleApplyCoupon}
            disabled={isApplyingCoupon}
            className="btn-primary"
          >
            Apply
          </Button>
        </div>

        {availableCoupons.length > 0 && (
          <div>
            <h3 className="font-medium text-gray-900 mb-3">Available Offers</h3>
            <div className="space-y-2">
              {availableCoupons.map(coupon => (
                <Card key={coupon.id} className="p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900">{coupon.code}</h4>
                      <p className="text-sm text-gray-600">{coupon.description}</p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => {
                        setCouponCode(coupon.code);
                        handleApplyCoupon();
                      }}
                      className="btn-primary"
                    >
                      Apply
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex space-x-3">
        <Button
          onClick={() => setStep(2)}
          variant="outline"
          className="flex-1"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button
          onClick={() => setStep(4)}
          className="flex-1 btn-primary"
        >
          Continue
        </Button>
      </div>
    </motion.div>
  );

  const OrderSummaryStep = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-6"
    >
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Order Summary</h2>
        <p className="text-gray-600">Review your order before placing</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <MapPin className="w-5 h-5 mr-2" />
            Delivery Address
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1">
            <p className="font-medium">{deliveryAddress.name}</p>
            <p className="text-gray-600">{deliveryAddress.address}</p>
            {deliveryAddress.landmark && (
              <p className="text-gray-600">Near {deliveryAddress.landmark}</p>
            )}
            <p className="text-gray-600">{deliveryAddress.city} - {deliveryAddress.pincode}</p>
            <p className="text-gray-600">{deliveryAddress.phone}</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Order Items</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {cartItems.map(item => {
              // Ensure price is a number
              const itemPrice = typeof item.price === 'object' ? 
                (item.price[item.portion] || item.price.half || 0) : 
                (item.price || 0);
              
              return (
                <div key={`${item.id}-${item.portion}`} className="flex justify-between items-center">
                  <div>
                    <p className="font-medium">{item.name} ({item.portion})</p>
                    <p className="text-sm text-gray-600">Qty: {item.quantity}</p>
                  </div>
                  <p className="font-medium">{formatPrice(itemPrice * item.quantity)}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-2">
          <div className="flex justify-between">
            <span>Subtotal</span>
            <span>{formatPrice(getCartTotal())}</span>
          </div>
          {appliedCoupon && (
            <div className="flex justify-between text-green-600">
              <span>Discount ({appliedCoupon.code})</span>
              <span>-{formatPrice(getCartDiscount())}</span>
            </div>
          )}
          <div className="flex justify-between text-lg font-bold border-t pt-2">
            <span>Total</span>
            <span className="text-orange-600">{formatPrice(getFinalTotal())}</span>
          </div>
        </CardContent>
      </Card>

      <div className="flex space-x-3">
        <Button
          onClick={() => setStep(3)}
          variant="outline"
          className="flex-1"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button
          onClick={handlePlaceOrder}
          disabled={isPlacingOrder}
          className="flex-1 btn-primary"
        >
          {isPlacingOrder ? 'Placing Order...' : 'Place Order'}
        </Button>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="mobile-container py-4">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/')}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-lg font-semibold text-gray-900">Checkout</h1>
          </div>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="bg-white border-b">
        <div className="mobile-container py-4">
          <div className="flex items-center justify-between">
            {[1, 2, 3, 4].map((stepNum) => (
              <div key={stepNum} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= stepNum ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  {step > stepNum ? <Check className="w-4 h-4" /> : stepNum}
                </div>
                {stepNum < 4 && (
                  <div className={`w-12 h-1 mx-2 ${
                    step > stepNum ? 'bg-orange-500' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="mobile-container py-6">
        {step === 1 && <LoginStep />}
        {step === 2 && <AddressStep />}
        {step === 3 && <CouponStep />}
        {step === 4 && <OrderSummaryStep />}
      </main>
    </div>
  );
};

export default CheckoutPage;
